<template>
    <div class="flex flex-col mt-4 pb-0 gap-4">
      <div class="flex flex-col pb-0 gap-4">
        <div class="flex flex-col text-center">
          <h5 class="text-md font-semibold mb-2">Verification required</h5>
          <p class="text-base">
            A one-time password has been sent to your mobile number
          </p>
          <V3ImageComponent src="icons/otp-identifier.png" class="h-10 w-28 self-center mt-4" />
        </div>
        <div class="flex flex-col bg-gray-50 dark:bg-gray-800 pb-8 p-5 items-center">
          <UForm class="space-y-8 w-full py-2">
            <UFormGroup name="otp" label="One Time Password">
              <UInput type="number" v-model="otp" size="lg" placeholder="Enter 6-digit OTP" @keydown="handleOtpInput"
                @wheel="$event.target.blur()" />
            </UFormGroup>
            <div class="flex flex-col items-center">
              <p class="inline-flex items-center text-danger-700 self-start gap-2" v-if="error.length > 0">
                <V3ImageComponent src="icons/danger.png" class="h-3 w-3" />
                <span>{{ error }}</span>
              </p>
              <div class="w-48">
                <UFormGroup>
                  <UButton type="button" label="Submit" size="lg" block :loading="isChecking" @click="verifyOTP" />
                  <div class="w-full mt-4 text-center">
                    <p class="text-sm text-gray-500" v-if="!resendDisabled">
                      OTP sent Didn't receive?
                      <span class="text-primary font-semibold cursor-pointer" @click="resendOTP"
                        :class="{ 'text-gray-400 cursor-not-allowed': resendDisabled }">
                        Resend OTP
                      </span>
                    </p>
                    <p v-if="resendDisabled" class="text-sm text-gray-500 mt-2">
                      Resend OTP available in {{ timer }} seconds
                    </p>
                  </div>
                </UFormGroup>
              </div>
            </div>
          </UForm>
        </div>
      </div>
    </div>
  </template>
  
  <script setup lang="ts">

  const resendDisabled = ref(false);
  const timer = ref(30);
  const resendCount = ref(0);
  const maxResendAttempts = 2;
  const auth = useAuth();
  
  declare global {
    interface Window {
      BflAutoReadOTP: (otp: string) => void;
    }
  }
  
  const BflAutoReadOTP = (receivedOtp: any) => {
    otp.value = receivedOtp;
    setTimeout(() => {
      verifyOTP();
    }, 100);
  };
  
  window.BflAutoReadOTP = BflAutoReadOTP;
  
  const handleOtpInput = (event: KeyboardEvent) => {
    if (otp.value) {
      const value = otp.value.toString()
      if (value.length > 5 && event.key !== "Backspace") {
        event.preventDefault();
        return;
      }
    }
  };
  
  const verifyOTP = async () => {
    error.value = '';
  
    if (otp.value) {
      const value = otp.value.toString();
      if (value.length !== 6) {
        error.value = 'Please provide 6 digits OTP';
        return;
      }
    } else {
      error.value = 'Otp field cannot be empty';
      return;
    }
    isChecking.value = true;
    try {
      const apiResult: any = await useAPIHandler('post', '/auth/otp/verify', {
        body: { partner: runtimeConfig.public.partnerCode, phone: auth.user.phone, otp: otp.value }
      });
      const { access_token } = apiResult.data
  
      await auth.setAccessToken(access_token)
      setTimeout(() => emits('success'), 100);

    } catch (err) {
      console.log("error", err);
      error.value = 'Invalid OTP';
    } finally {
      isChecking.value = false;
    }
  };
  
  const runtimeConfig = useRuntimeConfig();
  
  const emits = defineEmits(['success']);
  const isLoading = ref(false);
  const isOTPSent = ref(false);
  const isChecking = ref(false);
  const error = ref('');
  const otp = ref<string | null>(null);  
  
  const startTimer = () => {
    resendDisabled.value = true;
    timer.value = 30;
    const interval = setInterval(() => {
      if (timer.value > 0) {
        timer.value--;
      } else {
        resendDisabled.value = false;
        clearInterval(interval);
      }
    }, 1000);
  };
  
  const resendOTP = async () => {
    if (resendDisabled.value || resendCount.value >= maxResendAttempts) {
      error.value = resendCount.value >= maxResendAttempts
        ? 'Maximum resend attempts reached. Please try again later.'
        : '';
      return;
    }
  
    error.value = '';
    isLoading.value = true;
    try {
      await sendOTP(auth.user.first_name, auth.user.phone);
      resendCount.value++;
      startTimer();
    } catch (err) {
      error.value = 'Failed to resend OTP. Please try again later.';
    } finally {
      isLoading.value = false;
    }
  };
  
  const sendOTP = async (name: string, phone: string) => {
    error.value = '';
    try {
      const { error } = await useAPIHandler('post', '/auth/otp', {
        body: { partner: runtimeConfig.public.partnerCode, name, phone }
      });
      if (error) {
        throw new Error();
      }
      isOTPSent.value = true;
      startTimer();
    } catch (err) {
      error.value = 'Failed to send OTP. Please try after sometime.';
    } finally {
      isLoading.value = false;
    }
  };
  
  onMounted( async () => await sendOTP(auth.user.first_name, auth.user.phone))
  
  </script>
  